import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { FC, MouseEvent, PropsWithChildren, ReactNode, useCallback } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled, { css, keyframes } from 'styled-components';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import TableTH from 'dashboard/layouts/TableLayout/TableTH';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Table, { TableCell, TableHead, TableRow } from 'design-system/components/Table';
import { TransitionDuration } from 'design-system/variables/transitions';

/**
 * Usage:
 *
 * <TableLayout
 *   isLoading={isLoading}
 *   isEmpty={inquiries.length === 0}
 *   headerTR={
 *     <>
 *       <TableTH>Header #1</TableTH>
 *       <TableTH>Header #2</TableTH>
 *     </>
 *   }
 *   nullState={<NullState />}
 * >
 *   <TableTR>
 *     <TableTD>info</TableTD>
 *     <TableTD>info</TableTD>
 *   </TableTR>
 *   <TableLinkTR to='...'>
 *     <TableTD>info</TableTD>
 *     <TableTD>info</TableTD>
 *   </TableLinkTR>
 * </TableLayout>
 */
const Container = styled.div<{ maxHeight?: number }>`
  position: relative;
  z-index: 0;
  overflow-x: auto;
  ${({ maxHeight }) => {
    if (maxHeight != null) {
      return `max-height: ${maxHeight}px;`;
    }
  }}
`;
const slideKeyframes = keyframes`
  0% {
    transform: translateX(-50%) scaleX(0);
  }
  50% {
    transform: translateX(0) scaleX(1);
  }
  100% {
    transform: translateX(50%) scaleX(0);
  }
`;
const Loader = styled.div`
  position: absolute;
  z-index: ${Z_INDICES.loadingOverlays};
  border-bottom: 3px solid ${({ theme }) => theme.color.primary.brand};
  width: 100%;

  animation: ${slideKeyframes} 3s infinite cubic-bezier(0.5, 0, 0.5, 1);
`;
const Overlay = styled.div`
  position: absolute;
  z-index: ${Z_INDICES.loadingOverlays};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.color.primary.white};
  opacity: 50%;
`;

interface TableLayoutProps {
  className?: string;
  containerMaxHeight?: number;
  dataTest?: string;
  headerTR: JSX.Element;
  isEmpty?: boolean;
  isLoading?: boolean;
  nullState: ReactNode;
  showHeader?: boolean;
}

const TableLayout: FC<PropsWithChildren<TableLayoutProps>> = ({
  children,
  className,
  containerMaxHeight,
  dataTest,
  headerTR,
  isEmpty,
  isLoading,
  nullState,
  showHeader,
}) => {
  if (isEmpty && !showHeader) {
    if (isLoading) {
      return <LoadingIndicator />;
    } else {
      return <Container data-test={dataTest}>{nullState}</Container>;
    }
  }

  return (
    <Container data-test={dataTest} maxHeight={containerMaxHeight} className={className}>
      {isLoading && <Loader data-test='table-layout__loader' />}
      {isLoading && <Overlay />}

      <Table>
        <TableHead>
          <TableRow>{headerTR}</TableRow>
        </TableHead>
        {!isEmpty && children}
      </Table>

      {isEmpty && nullState}
    </Container>
  );
};
export default TableLayout;

export const TableTR = styled(TableRow)<{ disableHoverBackground?: boolean }>`
  ${({ disableHoverBackground }) =>
    disableHoverBackground &&
    css`
      &:hover {
        background-color: transparent !important;
      }
    `}

  ${({ onClick, disableHoverBackground }) =>
    onClick != null &&
    !disableHoverBackground &&
    css`
      cursor: pointer;
      :hover {
        background-color: ${({ theme }) => theme.color.background.highlight};
      }
      transition-duration: ${TransitionDuration.Fast};
    `}
`;

export const TableLinkTR = styled(Link).attrs(() => ({ role: 'row' }))`
  display: table-row;

  cursor: pointer;
  :hover {
    background-color: ${({ theme }) => theme.color.background.highlight};
  }
  transition-duration: ${TransitionDuration.Fast};
`;

export const TableTD = styled(TableCell)<{ unclickable?: boolean }>`
  white-space: normal;
  ${(props) =>
    props.unclickable &&
    css`
      cursor: default;
    `}
`;

export const TableIconTD = styled(TableTD)`
  text-align: right;
  line-height: 0; /* To center icon; cannot use flex here */
  padding: 0;
  padding-left: ${Spacing[4]};
  width: 36px;
`;

export const TableActionTD = styled(TableTD)`
  text-align: right;
  padding: 0;
  padding-right: ${Spacing[4]};
  width: 48px;
`;

export const StickyTD = styled(TableTD)<{
  left?: number;
  hasRightBorder?: boolean;
}>`
  position: sticky;
  left: ${({ left = 0 }) => `${left}px`}; // Default to 0 if not specified
  background-color: inherit;

  ${({ hasRightBorder, theme }) =>
    hasRightBorder && `border-right: 2px solid ${theme.color.border.primary};`}
  z-index: 2;
`;

export const StickyTH = styled(TableTH)<{ left?: number; hasRightBorder?: boolean }>`
  position: sticky;
  left: ${({ left = 0 }) => `${left}px`}; // Default to 0 if not specified
  z-index: 2;
  max-width: 10%;
  ${({ hasRightBorder, theme }) =>
    hasRightBorder && `border-right: 2px solid ${theme.color.border.primary};`}
`;

interface TableRemoveRowProps {
  onClick?: () => void;
  id?: string;
  'data-test'?: string;
}

export const TableRemoveRow = ({ onClick, id, 'data-test': dataTest }: TableRemoveRowProps) => {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (onClick != null) {
        onClick();
      }
    },
    [onClick],
  );
  return (
    <IconButton
      width='icon'
      variant='basic'
      type='button'
      onClick={handleClick}
      id={id}
      data-test={dataTest}
    >
      <XIcon />
    </IconButton>
  );
};

const IconButton = styled(Button)`
  svg {
    height: unset;
    width: unset;
  }
`;
